/**
 * Z6 kill feed (09 §1): rows from the event stream. Newest at bottom, max 4,
 * rows live 6 s (hub 4 s) then un-draw over 120 ms; a rescinded row is
 * struck through with RESCINDED for 2 s, then un-draws. Pure.
 */
import type { Team } from '../../../sim/types.js'
import type { SimEvent } from '../../../shared/events.js'
import type { FeedRowData } from './components.js'

export const FEED_LIFE_MS = 6000
export const FEED_LIFE_HUB_MS = 4000
export const FEED_UNDRAW_MS = 120
export const FEED_RESCIND_MS = 2000
export const FEED_MAX = 4

export interface FeedRow { key: string; at: number; data: FeedRowData; struckAt: number | null }
export interface VisibleFeedRow extends FeedRow { alpha: number; progress: number; strike: boolean }

export class KillFeed {
  private rowsList: FeedRow[] = []
  /** discId → playerId of the last perfect deflect (RIPOSTE attribution). */
  private lastPerfect = new Map<number, { playerId: string; tick: number }>()

  ingest(events: SimEvent[], now: number, names: (id: string) => string, teamOf: (id: string) => Team, opts: { hub?: boolean } = {}): void {
    void opts
    const derezzes = events.filter((e) => e.type === 'derez') as Extract<SimEvent, { type: 'derez' }>[]
    const teams = new Set(derezzes.map((d) => teamOf(d.playerId)))
    const doubleKo = derezzes.length >= 2 && teams.size === 2
    for (const e of events) {
      switch (e.type) {
        case 'deflectPerfect': this.lastPerfect.set(e.discId, { playerId: e.playerId, tick: e.tick }); break
        case 'throw': this.lastPerfect.delete(e.discId); break
        case 'hit': {
          const lp = this.lastPerfect.get(e.discId)
          const riposte = !!lp && lp.playerId === e.attackerId && lp.tick <= e.tick
          const tags = [e.bank ? 'BANK' : '', riposte ? 'RIPOSTE' : ''].filter(Boolean)
          this.add({ key: `${e.tick}:${e.victimId}`, at: now, struckAt: null, data: { attacker: { name: names(e.attackerId), team: teamOf(e.attackerId) }, victim: { name: names(e.victimId), team: teamOf(e.victimId) }, tags, posthumous: e.posthumous } })
          break
        }
        case 'pickup':
          if (e.stolen) this.add({ key: `steal:${e.tick}:${e.playerId}`, at: now, struckAt: null, data: { attacker: null, victim: { name: names(e.playerId), team: teamOf(e.playerId) }, tags: ['STEAL'] } })
          break
        case 'rescind': {
          const r = this.rowsList.find((x) => x.key === `${e.originalTick}:${e.playerId}`)
          if (r) { r.struckAt = now; if (!r.data.tags.includes('RESCINDED')) r.data.tags = [...r.data.tags, 'RESCINDED'] }
          break
        }
        default: break
      }
    }
    if (doubleKo) {
      // One DOUBLE DEREZ row replaces the two hit rows of that batch.
      const keys = new Set(derezzes.map((d) => `${d.tick}:${d.playerId}`))
      this.rowsList = this.rowsList.filter((r) => !keys.has(r.key))
      this.add({ key: `double:${derezzes[0].tick}`, at: now, struckAt: null, data: { attacker: null, victim: { name: '', team: 0 }, tags: [], double: true } })
    }
  }

  private add(r: FeedRow): void {
    this.rowsList.push(r)
    if (this.rowsList.length > FEED_MAX) this.rowsList.splice(0, this.rowsList.length - FEED_MAX)
  }

  /** Visible rows oldest→newest with alpha/un-draw progress. */
  rows(now: number, opts: { hub?: boolean } = {}): VisibleFeedRow[] {
    const life = opts.hub ? FEED_LIFE_HUB_MS : FEED_LIFE_MS
    const out: VisibleFeedRow[] = []
    this.rowsList = this.rowsList.filter((r) => {
      const end = r.struckAt !== null ? Math.min(r.at + life, r.struckAt + FEED_RESCIND_MS) : r.at + life
      return now < end + FEED_UNDRAW_MS
    })
    for (const r of this.rowsList) {
      const end = r.struckAt !== null ? Math.min(r.at + life, r.struckAt + FEED_RESCIND_MS) : r.at + life
      const undraw = now >= end ? 1 - Math.min(1, (now - end) / FEED_UNDRAW_MS) : 1
      out.push({ ...r, alpha: 1, progress: undraw, strike: r.struckAt !== null })
    }
    return out
  }

  clear(): void { this.rowsList = []; this.lastPerfect.clear() }
}
